import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Select, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Moment } from 'moment';
import moment from 'moment'
import 'moment/locale/pt-br';
import { useState } from 'react';
import PaperDraggable from '../PaperDraggable';
import AddIcon from '@mui/icons-material/Add';

export default function MultipleDatePicker({
    label,
    value = null,
    onChange,
    blockFutureDates = false,
    blockPastDates = false,
    error = false,
    views = ['year', 'month', 'day'],
    formatString = 'DD/MM/YYYY',
    disabled = false,
    pastDates = moment(),
    futureDates = moment().subtract(1, 'day'),
    sizeInput = 'medium',
    required = false
}: MultipleDatePickerProps) {

    const yesterday: Moment | undefined = blockFutureDates ? futureDates : undefined
    const today: Moment | undefined = blockPastDates ? pastDates : undefined

    const [openModal, setOpenModal] = useState(false)

    //Funções manipuladoras
    const handleConfirm = (data: Moment[]) => {
        onChange?.(data)
        setOpenModal(false)
    }

    const handleChange = (data: Moment[] | null) => {
        onChange?.(data)
    }
    //

    return (
        <>
            <FormControl
                {...{ required }}
                fullWidth
            >
                <InputLabel {...{ required }} id="multiple-date-picker-label">{label}</InputLabel>
                <Select
                    {...{ required }}
                    labelId="multiple-date-picker-label"
                    label={label}
                    multiple
                    readOnly
                    value={value}
                    IconComponent={() => null}
                    onClick={() => setOpenModal(true)}
                    renderValue={() => (
                        value &&
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {value.map((value) => (
                                <Chip key={value.format(formatString)} label={value.format(formatString)} />
                            ))}
                        </Box>
                    )}
                    sx={{ maxHeight: 56}}
                >
                </Select>
            </FormControl>
            {
                openModal &&
                <DialogMultipleDatePicker
                    open={openModal}
                    label={label}
                    yesterday={yesterday}
                    today={today}
                    views={views}
                    value={value}
                    onClose={() => setOpenModal(false)}
                    handleConfirm={handleConfirm}
                    onChange={handleChange}
                />
            }
        </>
    );
}

function DialogMultipleDatePicker({
    open,
    label,
    value,
    yesterday,
    formatString = 'DD/MM/YYYY',
    today,
    views,
    onClose,
    handleConfirm,
    onChange
}: DialogMultipleDatePickerProps) {

    const [selectedDate, setSelectedDate] = useState<Moment | null>(null)
    const [internalValue, setInternalValue] = useState<Moment[]>(value || [])

    const handleAddDate = () => {
        if (selectedDate && !internalValue.find(date => date.isSame(selectedDate, 'day'))) {
            const newDates = [...internalValue, selectedDate].sort((a, b) => a.valueOf() - b.valueOf())
            setInternalValue(newDates)
            setSelectedDate(null)
        }
    }
    return (
        <Dialog
            fullWidth
            maxWidth="xs"
            open={open}
            onClose={() => onClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {label}
            </DialogTitle>

            <DialogContent>
                <Stack direction="row" spacing={1} alignItems="center">
                    <DatePicker
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        maxDate={yesterday}
                        minDate={today}
                        views={views}
                        slotProps={{
                            textField: {
                                inputProps: {
                                    readOnly: true,
                                    style: { cursor: 'pointer' }
                                }
                            }
                        }}
                        sx={{ width: '100%' }}
                    />
                    <IconButton color="primary" onClick={handleAddDate}>
                        <AddIcon />
                    </IconButton>
                </Stack>

                <Box width={'100%'} display={'flex'} flexWrap={'wrap'} gap={1} mt={2}>
                    {
                        internalValue &&
                        internalValue?.map((date, index) => (
                            <Box key={index}>
                                <Chip label={date.format(formatString)}
                                    onDelete={() => {
                                        setInternalValue(internalValue.filter((_, i) => i !== index))
                                    }}
                                />
                            </Box>
                        ))}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={() => {
                    onClose()
                    onChange(value || [])
                }}>
                    Cancelar
                </Button>
                
                <Button
                    variant="contained"
                    onClick={() => handleConfirm(internalValue)}
                    disabled={internalValue.length === 0}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
interface MultipleDatePickerProps {
    label: string;
    value?: Moment[] | null;
    blockFutureDates?: boolean
    blockPastDates?: boolean
    onChange?: (data: Moment[] | null) => void;
    views?: Array<"year" | "month" | "day">,
    formatString?: string
    disabled?: boolean
    required?: boolean
    pastDates?: Moment
    futureDates?: Moment
    error?: boolean,
    sizeInput?: 'small' | 'medium'
}

interface DialogMultipleDatePickerProps {
    open: boolean
    label: string
    value: Moment[] | null
    yesterday: Moment | undefined
    today: Moment | undefined
    formatString?: string
    views: Array<"year" | "month" | "day">
    handleConfirm: (data: Moment[]) => void
    onChange: (data: Moment[] | null) => void
    onClose: () => void
}